import React, { useEffect } from "react";
import { SwipeableDrawer } from "@material-ui/core";
import { useSideNavState } from "../../../zust";
import { XIcon } from "@heroicons/react/outline";
import { AdminNavLinks, NavLinks } from "./NavLinks";
import { HiMenuAlt1 } from "react-icons/hi";
import { Link } from "react-router-dom";
import { FiLogOut } from "react-icons/fi"; // Used in your other components
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../../redux/action";

export const AuthNav = ({
  title,
  showLogo = false,
  showMenu = true,
  className = "",
  onMenuClick,
}) => {
  const { sideNavState, setSideNavState } = useSideNavState();

  useEffect(() => {
    setSideNavState(false);
  }, []);

  const handleMenuClick = () => {
    if (onMenuClick) {
      onMenuClick();
    } else {
      setSideNavState(!sideNavState);
    }
  };

  return (
    <div
      className={`
        flex flex-row h-18 z-30 bg-white px-3 fixed py-4 w-full  ${className}
      `}
    >
      <div className="flex-none flex items-center">
        {showMenu && (
          <HiMenuAlt1
            onClick={handleMenuClick}
            className="h-8 cursor-pointer lg:mr-6 text-3xl"
          />
        )}

        {showLogo && (
          <img className="h-8" src={"/img/logo/icon.svg"} alt="Logo" />
        )}
      </div>

      {title && (
        <div className="flex-1 flex items-center justify-center">
          <h1 className="text-lg font-body font-semibold text-gray-800">
            {title}
          </h1>
        </div>
      )}
    </div>
  );
};

export const SignNav = () => {
  const { sideNavState, setSideNavState } = useSideNavState();
  return (
    <div
      className="
   flex  flex-row h-18  z-30 px-2 md:px-8 py-4 w-full  border 
"
    >
      <div className="flex-none flex">
        <Link to="/">
          <img
            className="h-12 w-auto object-contain"
            src="/img/logo/logo.png"
          ></img>
        </Link>
      </div>
    </div>
  );
};

export const SideNav = () => {
  const { sideNavState, setSideNavState } = useSideNavState();
  const dispatch = useDispatch();

  // Fetch user data to populate the profile section
  const { user } = useSelector((state) => state.userData) || {
    user: { firstName: "Mark", lastName: "Johnson" }, // Fallback
  };

  useEffect(() => {
    setSideNavState(false);
  }, [setSideNavState]);

  return (
    <SwipeableDrawer
      anchor={"left"}
      open={sideNavState}
      onClose={() => setSideNavState(false)}
      onOpen={() => setSideNavState(true)}
      ModalProps={{
        keepMounted: false,
      }}
    >
      <div
        className="w-72 h-full bg-[#1C1D21] text-gray-300 flex flex-col font-body"
        role="navigation"
        aria-label="Main navigation"
      >
        {/* Top Branding Section */}
        <div className="p-6 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <h2 className="font-bold text-lg text-white tracking-wide">
              Zam-chain Advanced investment
            </h2>
          </div>

          {/* Mobile Close Button */}
          <button
            onClick={() => setSideNavState(false)}
            className="md:hidden p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            <XIcon className="h-6 w-6 text-gray-500 hover:text-white" />
          </button>
        </div>

        {/* User Profile Section */}
        <div className="flex flex-col items-center mt-2 mb-8 px-6">
          <div className="w-24 h-24 bg-gray-600 rounded-full flex items-center justify-center text-3xl font-bold text-white shadow-lg mb-4">
            {/* Initial Fallback if no profile image */}
            {user?.firstName?.charAt(0)?.toUpperCase()}
            {user?.lastName?.charAt(0)?.toUpperCase()}
          </div>
          <p className="text-xs text-gray-400 mb-1">Welcome Back,</p>
          <h3 className="text-lg font-medium text-white">
            {user?.firstName} {user?.lastName}
          </h3>
        </div>

        {/* Navigation Links Section */}
        <div className="flex-1 px-4 overflow-y-auto text-center mx-auto">
          {/* You may need to update your <NavLinks /> component internally to have:
            - text-gray-400 for inactive links
            - text-white for active links
            - A subtle colored dot or accent border for the active state
          */}
          <NavLinks />
        </div>

        {/* Bottom Pin Section (Log Out) */}
        <div className="p-6 mt-auto">
          <button
            className="flex items-center w-full gap-4 px-4 py-3 text-gray-200 hover:text-white hover:bg-gray-800/50 rounded-xl transition-all duration-200 group"
            onClick={() => {
              dispatch(logout());
            }}
          >
            <div className="p-2 rounded-lg bg-gray-800 group-hover:bg-gray-700 transition-colors">
              <FiLogOut size={18} />
            </div>
            <span className="font-medium text-sm">Log Out</span>
          </button>
        </div>
      </div>
    </SwipeableDrawer>
  );
};

export const AdminSideNav = () => {
  const { sideNavState, setSideNavState } = useSideNavState();

  return (
    <SwipeableDrawer
      anchor={"left"}
      open={sideNavState}
      onClose={() => setSideNavState(false)}
      onOpen={() => setSideNavState(true)}
      className=""
      ModalProps={{
        // Prevent focus on hidden elements
        keepMounted: false,
      }}
    >
      <div
        className="w-80 h-full bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900 text-white"
        role="navigation"
        aria-label="Admin navigation"
      >
        {/* Admin Header Section */}
        <div className="p-6 border-b border-gray-700/50">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <div>
                <h2 className="font-bold text-lg">Admin Panel</h2>
                <p className="text-xs text-gray-300">Management Dashboard</p>
              </div>
            </div>
            <button
              onClick={() => setSideNavState(false)}
              className="p-2 hover:bg-gray-700/50 rounded-lg transition-colors duration-200"
              aria-label="Close admin navigation menu"
              tabIndex={sideNavState ? 0 : -1}
            >
              <XIcon className="h-5 w-5 text-gray-300 hover:text-white transition-colors" />
            </button>
          </div>

          {/* Admin Badge */}
          <div className="bg-gradient-to-r from-blue-500/30 to-purple-600/30 backdrop-blur-sm border border-indigo-500/30 rounded-lg p-3">
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
              <span className="text-sm font-medium text-gray-100">
                Administrator Access
              </span>
            </div>
          </div>
        </div>

        {/* Admin Navigation Section */}
        <div className="flex-1 px-4 py-6">
          <div className="space-y-2">
            <AdminNavLinks />
          </div>
        </div>

        {/* Admin Footer */}
        <div className="p-4 border-t border-gray-700/50">
          <div className="text-xs text-gray-300 text-center">
            <p>Admin Dashboard v2.0</p>
            <p className="mt-1 opacity-75">Secure Management Portal</p>
          </div>
        </div>
      </div>
    </SwipeableDrawer>
  );
};
